
import {$authHost,$host} from "./index";
import {getCategories, getSizeIdByName} from "./catalogApi";
import { getClientID } from "../components/YM/YM";

const preorderQuoteMessages = {400: 'Проверьте данные предзаказа.', 401: 'Войдите в аккаунт.',
    403: 'Нет доступа к оформлению предзаказа.', 409: 'Условия предзаказа изменились. Обновите расчёт.',
    429: 'Слишком много запросов. Повторите через минуту.', 503: 'Расчёт предзаказа временно недоступен.'};
const preorderCheckoutMessages = {...preorderQuoteMessages,
    503: 'Сохранение предзаказа временно недоступно. Проверьте результат.'};
const preorderPaymentMessages = {...preorderQuoteMessages,
    503: 'Подготовка оплаты предзаказа временно недоступна.'};
// The server admits one active preorder operation per owner. Order cards may
// mount together, so share one transport lane instead of making their reads
// reject each other. Never retry a command or alter ordinary checkout traffic.
let preorderRequestTail = Promise.resolve();
const inPreorderRequestLane = operation => {
    const result = preorderRequestTail.then(operation);
    preorderRequestTail = result.then(() => undefined, () => undefined);
    return result;
};
const readPreorderCalculation = async (path, body, version, checkout = false) => {
    const messages = checkout === 'payment' ? preorderPaymentMessages : checkout ? preorderCheckoutMessages : preorderQuoteMessages;
    try {
        const token = localStorage.getItem('token');
        const assertOwner = () => {
            if (typeof token !== 'string' || !token.length || localStorage.getItem('token') !== token)
                throw Object.assign(new Error(), {status: 401});
        };
        assertOwner();
        // Snapshot before the asynchronous auth interceptor. A retry uses the
        // same acceptance command; neither lane emits purchase analytics.
        const copied = ordinaryCopy(body);
        const response = await inPreorderRequestLane(() => {
            assertOwner();
            return $authHost.post(path, copied, {withCredentials: false,
                headers: {'Content-Type': 'application/json'},
                // Run after Axios' serializer and asynchronous auth interceptors:
                // a queued old-owner command must never use the next owner's token.
                transformRequest: [...[].concat($authHost.defaults.transformRequest || []), serialized => {
                    assertOwner();
                    return serialized;
                }]});
        });
        assertOwner();
        if (response.status !== 200 || response.data?.schemaVersion !== version) throw new Error();
        return response.data;
    } catch (cause) {
        const received = cause?.response?.status ?? cause?.status;
        const status = Object.prototype.hasOwnProperty.call(messages, received) ? received : 503;
        const error = new Error(messages[status]);
        error.status = status;
        throw error;
    }
};
export const readConfirmedPreorderOptions = target => readPreorderCalculation(
    '/api/order/confirmedpreorder/options', target, 'confirmed-preorder-options-v1');
export const readConfirmedPreorderCartOptions = target => readPreorderCalculation(
    '/api/order/confirmedpreorder/cartoptions', target, 'confirmed-preorder-cart-options-v1');
export const calculateConfirmedPreorderCdek = command => readPreorderCalculation(
    '/api/order/confirmedpreorder/cdekcalculate', command, 'confirmed-preorder-cdek-calculation-public-v1');
export const selectConfirmedPreorderCdek = command => readPreorderCalculation(
    '/api/order/confirmedpreorder/cdekselect', command, 'confirmed-preorder-cdek-selection-public-v1');
export const quoteConfirmedPreorder = draft => readPreorderCalculation(
    '/api/order/confirmedpreorder/quote', draft, 'confirmed-preorder-quote-v1');
export const acceptConfirmedPreorder = command => readPreorderCalculation(
    '/api/order/confirmedpreorder/accept', command, 'confirmed-preorder-checkout-v1', true);
export const recoverConfirmedPreorder = command => readPreorderCalculation(
    '/api/order/confirmedpreorder/recover', command, 'confirmed-preorder-checkout-v1', true);
export const prepareConfirmedPreorderPayment = command => readPreorderCalculation(
    '/api/order/confirmedpreorder/payment', command, 'confirmed-preorder-payment-v1', 'payment');
export const readConfirmedPreorderPaymentStatus = command => readPreorderCalculation(
    '/api/order/confirmedpreorder/paymentstatus', command, 'confirmed-preorder-payment-status-v1', 'payment');
export const readConfirmedPreorderHistory = command => readPreorderCalculation(
    '/api/order/confirmedpreorder/history', command, 'confirmed-preorder-history-v1');

const preorderLifecycleMessages = {...preorderQuoteMessages,
    503: 'Статус предзаказа временно недоступен. Проверьте результат.'};
const preorderCancellationMessages = {...preorderQuoteMessages,
    503: 'Отмена предзаказа временно недоступна. Проверьте результат.'};
const lifecycleUuid = value => typeof value === 'string' &&
    /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value);
const lifecycleHash = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const lifecycleRecord = (value, keys) => value && [Object.prototype, null].includes(Object.getPrototypeOf(value)) &&
    Reflect.ownKeys(value).length === keys.length && keys.every(key => {
        const descriptor = Object.getOwnPropertyDescriptor(value, key);
        return descriptor && Object.hasOwn(descriptor, 'value') && descriptor.enumerable;
    });
// These small additive lanes do not change the older checkout/payment transport.
// Capture input before auth awaits, and refuse a changed session before dispatch.
const requestPreorderLifecycle = async (path, body, cancellation) => {
    const messages = cancellation ? preorderCancellationMessages : preorderLifecycleMessages;
    const keys = ['acceptanceKey', 'expectedTermsSha256', ...(cancellation ? ['cancellationKey'] : [])];
    if (!lifecycleRecord(body, keys) || !lifecycleUuid(body.acceptanceKey) || !lifecycleHash(body.expectedTermsSha256) ||
        (cancellation && !lifecycleUuid(body.cancellationKey))) {
        const error = new Error(messages[400]); error.status = 400; throw error;
    }
    const copied = Object.fromEntries(keys.map(key => [key, body[key]]));
    try {
        const token = localStorage.getItem('token');
        const response = await inPreorderRequestLane(() => {
            if (localStorage.getItem('token') !== token) throw new Error();
            return $authHost.post(path, copied, {withCredentials: false,
                headers: {'Content-Type': 'application/json'},
                transformRequest: [(data, headers) => {
                    if (localStorage.getItem('token') !== token) throw new Error();
                    return $authHost.defaults.transformRequest[0](data, headers);
                }]});
        });
        const descriptor = response.data && Object.getOwnPropertyDescriptor(response.data, 'schemaVersion');
        const version = cancellation ? 'confirmed-preorder-cancellation-v1' : 'confirmed-preorder-lifecycle-v1';
        if (response.status !== 200 || !descriptor || !Object.hasOwn(descriptor, 'value') || descriptor.value !== version) throw new Error();
        return response.data;
    } catch (cause) {
        const status = Object.prototype.hasOwnProperty.call(messages, cause?.response?.status) ? cause.response.status : 503;
        const error = new Error(messages[status]); error.status = status; throw error;
    }
};
export const readConfirmedPreorderLifecycle = command => requestPreorderLifecycle(
    '/api/order/confirmedpreorder/lifecyclestatus', command, false);
export const cancelConfirmedPreorder = command => requestPreorderLifecycle(
    '/api/order/confirmedpreorder/cancel', command, true);
export const readConfirmedPreorderCancellation = command => requestPreorderLifecycle(
    '/api/order/confirmedpreorder/cancelstatus', command, true);

const ordinaryMessages = {400: 'Проверьте данные заказа.', 401: 'Войдите в аккаунт.', 403: 'Нет доступа к оформлению заказа.',
    409: 'Условия заказа изменились. Обновите расчёт.', 429: 'Повторите через минуту.',
    503: 'Оформление временно недоступно. Проверьте результат заказа.'};
const ordinaryCopy = (value, depth = 0) => {
    if (depth > 16) throw Error();
    if (value === null || ['string', 'boolean'].includes(typeof value)) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (!value || typeof value !== 'object') throw Error();
    const fields = Object.getOwnPropertyDescriptors(value), keys = Reflect.ownKeys(fields);
    if (Array.isArray(value)) {
        if (value.length > 500 || keys.length !== value.length + 1) throw Error();
        return Object.freeze(Array.from({length: value.length}, (_, n) => {
            const field = fields[n]; if (!field?.enumerable || !Object.hasOwn(field, 'value')) throw Error();
            return ordinaryCopy(field.value, depth + 1);
        }));
    }
    if (![Object.prototype, null].includes(Object.getPrototypeOf(value)) || keys.length > 32) throw Error();
    const result = {};
    for (const key of keys) {
        const field = fields[key];
        if (typeof key !== 'string' || ['__proto__', 'constructor', 'prototype'].includes(key) || !field.enumerable || !Object.hasOwn(field, 'value')) throw Error();
        result[key] = ordinaryCopy(field.value, depth + 1);
    }
    return Object.freeze(result);
};
const ordinaryRequest = async (path, command, options, quote = false) => {
    try {
        const capability = ordinaryCopy(options);
        if (Object.keys(capability).length !== 1 || !['guest', 'authenticated'].includes(capability.authScope)) throw Error();
        const scope = capability.authScope, token = localStorage.getItem('token');
        const identityCurrent = () => localStorage.getItem('token') === token &&
            (scope === 'guest' ? token === null : typeof token === 'string' && token.length > 0);
        if (!identityCurrent()) throw Object.assign(Error(), {status: 401});
        const body = ordinaryCopy(command);
        if (JSON.stringify(body).length > 1048576) throw Error();
        const client = scope === 'guest' ? $host : $authHost;
        const payload = path.endsWith('/createneworder') ? {ordinaryCheckout: body} :
            path.endsWith('/createautomaticpreorder') ? {automaticPreorderCheckout: body} : body;
        const response = await client.post(path, payload, {
            withCredentials: false, headers: {'Content-Type': 'application/json'},
            transformRequest: [...[].concat(client.defaults.transformRequest || []), serialized => {
                if (!identityCurrent()) throw Object.assign(Error(), {status: 401});
                return serialized;
            }],
        });
        if (!identityCurrent()) throw Object.assign(Error(), {status: 401});
        const value = ordinaryCopy(response.data);
        if (response.status !== 200 || (quote ? value.schemaVersion !== 'site-order-quote-v1' :
            !['success', ...(['/recoverordinaryorder', '/recoverautomaticpreorder'].some(suffix => path.endsWith(suffix)) ? ['not_found'] : [])].includes(value.status))) throw Error();
        return value;
    } catch (cause) {
        const received = cause?.response?.status ?? cause?.status;
        const status = Object.hasOwn(ordinaryMessages, received) ? received : 503;
        throw Object.assign(new Error(ordinaryMessages[status]), {status});
    }
};
export const quoteOrdinaryOrder = (calculation, options) => ordinaryRequest('/api/order/quoteordinaryorder', calculation, options, true);
export const createOrdinaryOrder = (command, options) => ordinaryRequest('/api/order/createneworder', command, options);
export const recoverOrdinaryOrder = (command, options) => ordinaryRequest('/api/order/recoverordinaryorder', command, options);
export const quoteAutomaticPreorder = (calculation, options) => ordinaryRequest('/api/order/quoteautomaticpreorder', calculation, options, true);
export const createAutomaticPreorder = (command, options) => ordinaryRequest('/api/order/createautomaticpreorder', command, options);
export const recoverAutomaticPreorder = (command, options) => ordinaryRequest('/api/order/recoverautomaticpreorder', command, options);

export const createNewOrder = async (orderData) => {
    const clientid = getClientID();
    let basketItems = null;
    const {data} = await $host.post('/api/order/createneworder', {orderData, clientid}, {
        // Observe the existing final serialization, without changing its bytes
        // or running caller getters/toJSON again. Recovery storage is not an
        // analytics source; optional snapshot failure cannot block this POST.
        transformRequest: [...[].concat($host.defaults.transformRequest || []), serialized => {
            try {
                const rows = typeof serialized === 'string' ? JSON.parse(serialized)?.orderData?.BasketItems : null;
                basketItems = Array.isArray(rows) ? rows : null;
            } catch {basketItems = null;}
            return serialized;
        }],
    });

    if(data.status === 'success') {
        if(basketItems) {
            // A saved order ACK must survive synchronous and asynchronous
            // category/size/provider analytics failures and never wait for them.
            Promise.resolve().then(() => {
            const categoryIds = []
            basketItems.forEach((item) => {
                const {product} = item.item
                categoryIds.push(...product.product_category)
            })

            return getCategories(categoryIds).then(async categoriesResponse => {
                let products = [];
                for(let oneProduct of basketItems) {
                    let category = ''
                    if (categoriesResponse.success) {
                        category = oneProduct.item.product.product_category.map((id) => categoriesResponse.data[id]).join('/')
                    }
                    let findSize = null;
                    if (oneProduct.item.child_product_size) {
                        findSize = oneProduct.item.child_product_size.find(element =>  element.size_name === oneProduct.currentSize);
                    }
                    if (!findSize) {
                        const sizeData = await getSizeIdByName(oneProduct.currentSize);
                        if (sizeData.success) {
                            findSize = sizeData.data
                        }
                    }
                    if (category && findSize) {
                        products.push(
                            {
                                id: String(oneProduct.item.id)+String(oneProduct.item.color.id)+findSize.id,
                                name: oneProduct.item.product.name,
                                price: oneProduct.item.sell_price ? oneProduct.item.sell_price : oneProduct.item.parrent_price,
                                category,
                                variant: oneProduct.item.color.color_name,
                                quantity: oneProduct.count,
                            }
                        )
                    }
                }
                if (products.length) {
                    window.dataLayer.push({
                        event: "ym-ecom",
                        eCategory: "ecommerce",
                        eAction: "purchase",
                        eLabel: "cart",
                        ecommerce: {
                            currencyCode: "RUB",
                            purchase: {
                                actionField: {
                                    id: data.order.custom_order_id,
                                    revenue: data.order.result_price - data.order.deliveryPrice,
                                    shipping: data.order.deliveryPrice
                                },
                                products
                            }
                        }
                    })
                }
            })
            }).catch(() => {});
        }
    }
    return data;
}


export const getOneOrder = async (id) => {
    const {data} = await $authHost.post('/api/order/getoneorder', {id})
    return  data;
}

export const getOneOrderStatus = async (id) => {
    const {data} = await $authHost.post('/api/order/getoneorderstatus', {id})
    return  data;
}

export const getAllOrders = async (limit,offset,getCount, pattern='') => {
    const {data} = await  $authHost.post("/api/order/getallorders",{limit,offset, getCount, pattern});
    return  data;
}
export const getAllOrderStatus = async () => {
    const {data} = await  $authHost.post("/api/order/getallstatus");
    return  data;
}
export const getAllPayments = async () => {
    const {data} = await  $authHost.post("/api/order/getallpayments");
    return  data;
}

export const changeOrderStatus = async (orderId,statusOrderId) => {
    const {data} = await  $authHost.post("/api/order/changeorderstatus",{orderId, statusOrderId});
    return  data;
}
export const changeDataAfterPayOrder = async (options) => {
    const {data} = await  $authHost.post("/api/order/changedataafterpay",{options});
    return  data;
}
export const changeOrderPayment = async (orderId,paymentId) => {
    const {data} = await  $authHost.post("/api/order/changeorderpayment",{orderId, paymentId});
    return  data;
}

export const changeOrderDelivery = async (orderId,deliveryId) => {
    const {data} = await  $authHost.post("/api/order/changeorderdelivery",{orderId, deliveryId});
    return  data;
}

export const checkActive = async () => {
    const {data} = await  $authHost.post("/api/order/checkactive");
    return  data;
}

export const createUserCmp = async (identificator,number) => {
    const {data} = await  $authHost.post("/api/order/createcmpuser",{identificator,number});
    return  data;

}

export const changeComment = async (orderId,commentInfo) => {
    const {data} = await  $authHost.post("/api/order/changeordercomment",{orderId, commentInfo});
    return  data;
}

export const dolyamiMethodCreate = async (order) => {
    const {data} = await  $authHost.post("/api/order/dolyamicreate",{order});
    return  data;
}

export const yandexMethodCreate = async (order) => {
    const {data} = await  $authHost.post("/api/order/ysplitcreate",{order});
    return  data;
}

export const yandexMethodFind = async (orderId) => {
    const {data} = await  $authHost.post("/api/order/ysplitfindone",{orderId});
    return  data;
}

export const refoundDolyami = async (order,items) => {
    const {data} = await $authHost.post("/api/order/rejectdolyami", {order,items})
    return data;
}

export const refoundYSplit = async (order,items) => {
    const {data} = await $authHost.post("/api/order/refoundysplit", {order,items})
    return data;
}

export const refoundDolyamiCh = async (order,items) => {
    const {data} = await $authHost.post("/api/order/rejectdolyamich", {order,items})
    return data;
}

export const refoundPartYSplit = async (order,items) => {
    const {data} = await $authHost.post("/api/order/refoundpartysplit", {order,items})
    return data;
}

export const refoundCP = async (order,items) => {
    const {data} = await $authHost.post("/api/order/rejectcp", {order,items})
    return data;
}
export const refoundCPCh = async (order,items) => {
    const {data} = await $authHost.post("/api/order/rejectcpch", {order,items})
    return data;
}

export const saveChangeDeliveryAddressFields = async (order) => {
    const {data} = await  $authHost.post("/api/order/savedeliveryaddressfields",{order});
    return  data;
}

export const createPreOrder = async (orderData,orderUserFields,UserAccountFields,deviceUUID,userAgent) => {
    const clientid = getClientID();
    const {data} = await $host.post('/api/order/createpreorder', {orderData,orderUserFields,UserAccountFields,clientid,deviceUUID,userAgent})

    const {product} = orderData.item
    getCategories(product.product_category).then(categoriesResponse => {
        let category = ''
        if (categoriesResponse.success) {
            category = product.product_category.map((id) => categoriesResponse.data[id]).join('/')
            let findSize = null;
            if (orderData.item.child_product_size) {
                findSize = orderData.item.child_product_size.find(item => item.size_name === orderData.currentSize)
            }
            if (findSize) {
                const products = [{
                    id: String(product.id)+String(orderData.colorId)+findSize.id,
                    name: product.name,
                    price: orderData.item.sell_price ? orderData.item.sell_price : orderData.item.parrent_price,
                    category,
                    variant: orderData.item.color.color_name,
                    quantity: orderData.count,
                }]

                window.dataLayer.push({
                    event: "ym-ecom",
                    eCategory: "ecommerce",
                    eAction: "add",
                    ecommerce: {
                        currencyCode: "RUB",
                        add: {products}
                    }
                })

                window.dataLayer.push({
                    event: "ym-ecom",
                    eCategory: "ecommerce",
                    eAction: "purchase",
                    eLabel: "preorder",
                    ecommerce: {
                        currencyCode: "RUB",
                        purchase: {
                            actionField: {
                                id: data.order.custom_order_id,
                                revenue: product.price,
                                shipping: 0
                            },
                            products
                        }
                    }
                })
            }

        }

    })

    return data;
}

export const createNotifyOfReceiptOrder = async (orderData, orderUserFields, UserAccountFields, cityInfo = null, isFastOrder = false) => {
    const clientid = getClientID();
    let api = '/api/order/createnotificationorder';
    if (isFastOrder) {
        api = '/api/order/createfastorder'
    }
    const {data} = await $host.post(api, {orderData,orderUserFields,UserAccountFields,clientid,cityInfo})
    // A stock notification is a subscription, not an ecommerce purchase.
    if (!isFastOrder) return data;
    const {product} = orderData.item

    getCategories(product.product_category).then(categoriesResponse => {
        let category = ''
        if (categoriesResponse.success) {
            category = product.product_category.map((id) => categoriesResponse.data[id]).join('/')
            let findSize = null;
            if (orderData.item.child_product_size) {
                findSize = orderData.item.child_product_size.find(item => item.size_name === orderData.currentSize)
            }
            if (findSize) {
                const products = [{
                    id: String(product.id)+String(orderData.colorId)+findSize.id,
                    name: product.name,
                    price: orderData.item.sell_price ? orderData.item.sell_price : orderData.item.parrent_price,
                    category,
                    variant: orderData.item.color.color_name,
                    quantity: orderData.count,
                }]
                window.dataLayer.push({
                    event: "ym-ecom",
                    eCategory: "ecommerce",
                    eAction: "add",
                    ecommerce: {
                        currencyCode: "RUB",
                        add: {products}
                    }
                })

                window.dataLayer.push({
                    event: "ym-ecom",
                    eCategory: "ecommerce",
                    eAction: "purchase",
                    eLabel: isFastOrder ? "buy_one_click" : "notification",
                    ecommerce: {
                        currencyCode: "RUB",
                        purchase: {
                            actionField: {
                                id: data.order.custom_order_id,
                                revenue: orderData.item.sell_price ? orderData.item.sell_price : orderData.item.parrent_price,
                                shipping: 0
                            },
                            products
                        }
                    }
                })
            }
        }
    })

    return data;
}
export const сreateSertificateOrder = async (preorderInfo,userData,UserAccountFields) => {
    const clientid = getClientID();
    const {data} = await $host.post('/api/order/createsertificateorder', {preorderInfo,userData,UserAccountFields,clientid})

    return data;
}

export const findSertificate = async (sertificateCode,orderInfo) => {
    const {data} = await $host.post('/api/order/findsertificate', {sertificateCode,orderInfo})
    return data;
}
export const payOrderSertificate = async (certificates, orderInfo) => {
    const {data} = await $host.post('/api/order/payordersertificate', {certificates, orderInfo})
    return data;
}
export const changeOrderUserFields = async (currentOrder) => {
    const {data} = await $authHost.post('/api/order/changeorderuserfield', {currentOrder})
    return data;
}
export const saveOrderCity = async (orderId,object) => {
    const {data} = await $authHost.post("/api/order/changeordercityinfo", {orderId,object})
    return data;
}


export const fetchChildProductsForOrder = async (childIdArray) => {
    const {data} = await $authHost.post('/api/order/fetchchildproductsfororder', {childIdArray})
    return data;
}

export const searchProductsBySearchValue = async (value) => {
    const {data} = await $authHost.post('/api/order/searchproductsbysearchvalue', {value})
    return data;
}

export const addNewItemToOrder = async (item,orderId) => {
    const {data} = await $authHost.post('/api/order/addnewitemtoorder', {item,orderId});
    return data;
}

export const saveChangesOrderBasketItems = async (items,orderId) => {
    const {data} = await $authHost.post('/api/order/savechangesorderbasketitems', {items,orderId})
    return data;
}
